import { useCallback, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'

export function useCheckOut(onSuccess?: () => void) {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [checkingOutIds, setCheckingOutIds] = useState<Set<string>>(new Set())
  const [errorId, setErrorId] = useState<string | null>(null)

  const checkOut = useCallback(
    async (checkInId: string, isGuest: boolean) => {
      setCheckingOutIds((prev) => new Set(prev).add(checkInId))
      setErrorId(null)
      try {
        const token = await getAccessTokenSilently()
        const endpoint = isGuest ? `guest-check-out/${checkInId}` : `check-out/${checkInId}`
        const res = await fetch(`${apiUrl}/${endpoint}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.status === 409) {
          setErrorId(checkInId)
          // still reload so the list refreshes (member is already gone)
          onSuccess?.()
          return
        }
        onSuccess?.()
      } finally {
        setCheckingOutIds((prev) => {
          const next = new Set(prev)
          next.delete(checkInId)
          return next
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAccessTokenSilently, onSuccess],
  )

  function isCheckingOut(checkInId: string) {
    return checkingOutIds.has(checkInId)
  }

  function isAlreadyCheckedOut(checkInId: string) {
    return errorId === checkInId
  }

  return { checkOut, isCheckingOut, isAlreadyCheckedOut }
}
